import { RealTimeAction } from '@/_core/fluxstore/types';
import { StorymapState } from '../types';
import { useStorymapStore } from '../useModule';
import {
    useBoardActivityStore,
    AddRealtimeActivity,
    LoadBoardActivities,
    IncrementUnseenBoardActivityCount,
    AcknowledgeBoardActivities,
} from '../../boardActivity/store';
import type { BoardActivityItem } from '../../boardActivity/store';
import { mapDtoPropsToCamelCase } from '@/_core/utils/mapDtos';
import { watch } from '@vue/composition-api';
import { shouldCountBoardActivityForBadge, shouldFollowBoardActivityChange } from '../../boardActivity/store/badgePolicy';

const { registerMiddleware } = useStorymapStore();

export class BoardActivityReceived extends RealTimeAction<StorymapState> {
    public static ACTION_TYPE = 'BoardActivityReceived';
    public activity: any;

    constructor() {
        super(BoardActivityReceived.ACTION_TYPE);
    }
}

/**
 * The Board Activity rows of one MCP batch call (`CreateStoryMapCards`), sent once per call by the bridge: rows in
 * input order (PascalCase DTOs), the call's operation id, the row count and the card of the last input index.
 */
export class BoardActivitiesReceived extends RealTimeAction<StorymapState> {
    public static ACTION_TYPE = 'BoardActivitiesReceived';
    public activities: any[] = [];
    public operationId: string | null = null;
    public count = 0;
    public lastCardId: string | null = null;

    constructor() {
        super(BoardActivitiesReceived.ACTION_TYPE);
    }
}

// Reconnect catch-up state — initialize from current connection status so that
// if the module loads after SignalR is already connected, the first disconnect/
// reconnect cycle correctly triggers a catch-up load.
const boardActivityStore = useBoardActivityStore();
const storymapStore = useStorymapStore();

let hasEverBeenConnected = storymapStore.connectionStatus?.value === 'connected';
let sawDisconnect = false;
const visibleActivityAcknowledgeDebounceMs = 750;
let visibleActivityAcknowledgeTimeout: ReturnType<typeof setTimeout> | null = null;
let pendingVisibleActivityAcknowledgement: { storymapId: string; activityId: string; datetime: number } | null = null;

const clearPendingVisibleActivityAcknowledgement = () => {
    if (visibleActivityAcknowledgeTimeout !== null) {
        clearTimeout(visibleActivityAcknowledgeTimeout);
        visibleActivityAcknowledgeTimeout = null;
    }
    pendingVisibleActivityAcknowledgement = null;
};

const flushPendingVisibleActivityAcknowledgement = () => {
    const pendingAcknowledgement = pendingVisibleActivityAcknowledgement;
    visibleActivityAcknowledgeTimeout = null;
    pendingVisibleActivityAcknowledgement = null;

    if (!pendingAcknowledgement || storymapStore.state.id !== pendingAcknowledgement.storymapId) {
        return;
    }

    boardActivityStore.dispatch(
        new AcknowledgeBoardActivities(
            pendingAcknowledgement.activityId,
            pendingAcknowledgement.storymapId
        )
    );
};

const scheduleVisibleActivityAcknowledgement = (activity: BoardActivityItem) => {
    const storymapId = storymapStore.state.id;
    if (!storymapId || !activity.id) {
        return;
    }

    const activityDatetime = Date.parse(activity.datetime || '') || 0;
    if (
        !pendingVisibleActivityAcknowledgement ||
        pendingVisibleActivityAcknowledgement.storymapId !== storymapId ||
        activityDatetime >= pendingVisibleActivityAcknowledgement.datetime
    ) {
        pendingVisibleActivityAcknowledgement = {
            storymapId,
            activityId: activity.id,
            datetime: activityDatetime,
        };
    }

    if (visibleActivityAcknowledgeTimeout !== null) {
        clearTimeout(visibleActivityAcknowledgeTimeout);
    }
    visibleActivityAcknowledgeTimeout = setTimeout(
        flushPendingVisibleActivityAcknowledgement,
        visibleActivityAcknowledgeDebounceMs
    );
};

// Watch connection status for reconnect catch-up (immediate: true so an
// already-connected session sets the baseline on the first tick)
watch(() => storymapStore.connectionStatus?.value, (newStatus) => {
    if (newStatus === 'connected') {
        if (hasEverBeenConnected && sawDisconnect && boardActivityStore.state.isPanelOpen) {
            boardActivityStore.dispatch(new LoadBoardActivities());
        }
        if (!hasEverBeenConnected) {
            hasEverBeenConnected = true;
        }
        sawDisconnect = false;
    } else if (hasEverBeenConnected) {
        sawDisconnect = true;
    }
}, { immediate: true });

// Reset flags on storymap change — re-seed from current connection status so that
// if SignalR stays connected, the connection watcher doesn't need to re-fire to
// rebuild the baseline.
watch(() => storymapStore.state.correlationId, () => {
    hasEverBeenConnected = storymapStore.connectionStatus?.value === 'connected';
    sawDisconnect = false;
    clearPendingVisibleActivityAcknowledgement();
});

const middleware = (_state: StorymapState, action: BoardActivityReceived): boolean => {
    const mappedActivity: BoardActivityItem = mapDtoPropsToCamelCase(action.activity);
    const currentUserIds = [
        initAppData.currentUser.currentUserId,
        initAppData.currentUser.currentUserShortId,
    ];

    if (!boardActivityStore.state.isPanelOpen) {
        if (shouldCountBoardActivityForBadge(mappedActivity, currentUserIds)) {
            boardActivityStore.dispatch(new IncrementUnseenBoardActivityCount(mappedActivity));
        }
        return false;
    }

    const shouldFollowActivity = boardActivityStore.state.isFollowChangesOn &&
        shouldFollowBoardActivityChange(mappedActivity, currentUserIds);

    boardActivityStore.dispatch(new AddRealtimeActivity(mappedActivity, shouldFollowActivity));
    scheduleVisibleActivityAcknowledgement(mappedActivity);

    // Follow-changes scroll
    if (
        shouldFollowActivity &&
        mappedActivity.cardId
    ) {
        // Dynamic import to avoid circular dependency
        import('../../boardActivity/composables/useScrollToCard').then(({ scrollToCard }) => {
            scrollToCard(mappedActivity.cardId);
        });
    }

    return false; // No storymap store reducer needed
};

const getCurrentUserIds = () => [
    initAppData.currentUser.currentUserId,
    initAppData.currentUser.currentUserShortId,
];

const getNewestActivity = (activities: BoardActivityItem[]): BoardActivityItem =>
    activities.reduce((newest, activity) =>
        (Date.parse(activity.datetime || '') || 0) >= (Date.parse(newest.datetime || '') || 0) ? activity : newest
    );

// One envelope per MCP batch call (AF3, AF4): the badge counts its rows once, the board follows at most once — to the
// last card of the call. A replayed envelope (same operation id) is ignored for the whole board session.
const activitiesMiddleware = (_state: StorymapState, action: BoardActivitiesReceived): boolean => {
    const mappedActivities: BoardActivityItem[] = (action.activities || [])
        .filter((activity) => activity != null)
        .map((activity) => mapDtoPropsToCamelCase(activity));
    if (mappedActivities.length === 0) {
        return false;
    }

    if (action.operationId) {
        const processedOperationIds = boardActivityStore.state.realtimeProcessedOperationIds;
        if (processedOperationIds.includes(action.operationId)) {
            return false;
        }
        processedOperationIds.push(action.operationId);
    }

    const currentUserIds = getCurrentUserIds();

    if (!boardActivityStore.state.isPanelOpen) {
        mappedActivities
            .filter((activity) => shouldCountBoardActivityForBadge(activity, currentUserIds))
            .forEach((activity) => boardActivityStore.dispatch(new IncrementUnseenBoardActivityCount(activity)));
        return false;
    }

    const lastActivity = mappedActivities[mappedActivities.length - 1];
    const shouldFollow = boardActivityStore.state.isFollowChangesOn &&
        shouldFollowBoardActivityChange(lastActivity, currentUserIds);

    mappedActivities.forEach((activity) => {
        boardActivityStore.dispatch(new AddRealtimeActivity(activity, shouldFollow && activity === lastActivity));
    });
    scheduleVisibleActivityAcknowledgement(getNewestActivity(mappedActivities));

    const followCardId = action.lastCardId || lastActivity.cardId;
    if (shouldFollow && followCardId) {
        // Dynamic import to avoid circular dependency
        import('../../boardActivity/composables/useScrollToCard').then(({ scrollToCard }) => {
            scrollToCard(followCardId);
        });
    }

    return false; // No storymap store reducer needed
};

//@ts-ignore
registerMiddleware(BoardActivityReceived.ACTION_TYPE, middleware);
//@ts-ignore
registerMiddleware(BoardActivitiesReceived.ACTION_TYPE, activitiesMiddleware);
